import React, { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import { playSound } from 'flow/actions/sounds-actions';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import RangeSlider from 'flow/components/ui-components/RangeSlider';
import useAppContext from 'flow/hooks/useAppContext';
import { formatTime } from 'flow/utils/time-utils';
import { twMerge } from 'tailwind-merge';

interface Props {
    uniqueId: string;
    duration: number;
    url: string;
    className?: string;
}

const AudioPlayer = ({ uniqueId, duration, url, className }: Props) => {
    const { i18next } = useAppContext();

    const [slider, setSlider] = useState(0);
    // Driven by the element's own play/pause events rather than read off
    // audio.current during render: pausing fires no timeupdate, so the old
    // read could leave the icon showing the wrong state until the next tick.
    const [isPlaying, setIsPlaying] = useState(false);
    const audio = useRef<HTMLAudioElement>(null);

    useEffect(() => {
        const timeUpdate = () => {
            setSlider((audio.current?.currentTime / duration) * 100);

            if (audio.current?.currentTime >= duration) {
                audio.current.pause();
            }
        };

        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);

        const element = audio.current;

        element.addEventListener('timeupdate', timeUpdate, false);
        element.addEventListener('play', onPlay, false);
        element.addEventListener('pause', onPause, false);
        element.addEventListener('ended', onPause, false);

        return () => {
            element?.removeEventListener('timeupdate', timeUpdate, false);
            element?.removeEventListener('play', onPlay, false);
            element?.removeEventListener('pause', onPause, false);
            element?.removeEventListener('ended', onPause, false);
        };
    }, [uniqueId, duration]);

    useEffect(() => {
        audio.current.src = url;
        audio.current.load();

        return () => {
            if (audio.current) {
                audio.current.pause();
                audio.current.currentTime = 0;
            }
        };
    }, [uniqueId]);

    const onChangeSlider: ChangeEventHandler<HTMLInputElement> = (event) => {
        const percentage = parseFloat(event.target.value);
        audio.current.currentTime = duration * (percentage / 100);
        setSlider(percentage);
    };

    const play = () => {
        playSound(uniqueId);
        document
            .querySelectorAll('.audio-player-pause')
            .forEach((pauseButton) => {
                (pauseButton as HTMLElement).click();
            });

        if (audio.current.currentTime >= duration) {
            audio.current.currentTime = 0;
        }

        audio.current.play();
    };

    return (
        <div
            className={twMerge(
                'flex grow items-center gap-2 rounded-sm bg-gray-200 p-1',
                className,
            )}
            dir="ltr"
        >
            <audio ref={audio} preload="none">
                <source src={url} />
                <track kind="captions" />
            </audio>

            {/*
             * One toggle button rather than two swapped buttons: a screen
             * reader announces "Play, pressed" while playing, so the state is
             * carried by aria-pressed instead of by which icon is drawn. The
             * previous pause button had no name at all, and both branches
             * hardcoded aria-pressed={false}, which was always wrong.
             * The audio-player-pause class stays conditional so the
             * pause-everything-else sweep in play() only ever finds players
             * that are actually playing.
             */}
            <IconButton
                onClick={isPlaying ? () => audio.current.pause() : play}
                tooltipLabel={i18next.t('Tooltip.Audio.Play')}
                className={twMerge(
                    'p-0',
                    isPlaying ? 'audio-player-pause' : '',
                )}
                aria-label={i18next.t('Tooltip.Audio.Play')}
                aria-pressed={isPlaying}
            >
                <Icon
                    iconKey={isPlaying ? 'pause' : 'play_arrow'}
                    className="text-gray-600"
                    size="1.25rem"
                />
            </IconButton>

            <RangeSlider
                handleChange={onChangeSlider}
                preferredValue={slider}
                ariaLabel={i18next.t('audio.player.position')}
                // Derived from the same slider value that feeds aria-valuenow,
                // rather than read off the element mid-render. A ref read is
                // not reactive: on a render the slider did not cause, the two
                // would describe different instants of the same audio.
                ariaValueText={`${formatTime(
                    Math.floor((slider / 100) * duration),
                )} / ${formatTime(Math.floor(duration))}`}
            />

            <div className="shrink-0 pe-1 text-center text-xs text-gray-500 tabular-nums">
                {formatTime(Math.floor(audio.current?.currentTime || 0))}
            </div>
        </div>
    );
};

export default AudioPlayer;
